/**
 * Default, configurable templates seeded for a new workspace.
 *
 * These are ordinary {@link Template} drafts — nothing about them is special or
 * hardcoded into the engine. A first-run workspace is seeded with them so users
 * have something to estimate against immediately; they can use them as-is, edit
 * them (which creates a new version), or author their own from scratch.
 *
 * - {@link DEFAULT_ESTIMATION_TEMPLATE}: the "B + C + W" effort model — a Base
 *   engineering effort (plus dependency risk and a review buffer), Confidence-
 *   adjusted, then extended by Waiting/calendar time.
 * - {@link DEFAULT_TASK_TEMPLATE}: a per-task work-item schema — title,
 *   description, acceptance criteria, story points, priority and status.
 *
 * Pure TS — no React, no I/O. Seeding is performed by {@link seedDefaultTemplates}
 * through the {@link TemplateStore} (facade only — strict layering).
 */

import type { Template } from './types';
import type { TemplateStore } from './templateStore';

/** Draft shape the TemplateStore.create accepts. */
export type TemplateSeed = Pick<
  Template,
  'name' | 'description' | 'fields' | 'outputs'
>;

/** Logical name of the default B+C+W estimation template. */
export const DEFAULT_ESTIMATION_TEMPLATE_NAME = 'Effort Estimate (B+C+W)';

/** Logical name of the default task template. */
export const DEFAULT_TASK_TEMPLATE_NAME = 'Task';

/**
 * The default "B + C + W" effort-estimation template.
 *
 * A **B**ase engineering effort (plus dependency risk and a review buffer) is
 * **C**onfidence-adjusted (lower confidence inflates the days) and finally
 * extended by **W**aiting/calendar time:
 *
 *   engineeringEffortDays  = (baseEngineeringDays + dependencyRiskDays)
 *                            * (1 + reviewBufferPercent / 100)
 *   confidenceAdjustedDays = engineeringEffortDays * (100 / confidencePercent)
 *   calendarDays           = confidenceAdjustedDays + calendarWaitingDays
 *
 * Nothing here is hardcoded into the engine — it is an ordinary, fully editable
 * Template document whose expressions are evaluated by the browser-side engine
 * (which supports `+ - * /` and references to earlier outputs).
 */
export const DEFAULT_ESTIMATION_TEMPLATE: TemplateSeed = {
  name: DEFAULT_ESTIMATION_TEMPLATE_NAME,
  description:
    'Base engineering effort (B) + dependency risk and review buffer, Confidence-adjusted (C), then extended by Waiting/calendar time (W). Fully configurable — edit to create a new version.',
  fields: [
    {
      key: 'baseEngineeringDays',
      label: 'Base engineering (person-days)',
      type: 'number',
      defaultValue: 5,
      min: 0,
      group: 'B — Base',
      helpText: 'Hands-on engineering effort assuming everything goes to plan.',
    },
    {
      key: 'dependencyRiskDays',
      label: 'Dependency / risk (person-days)',
      type: 'number',
      defaultValue: 2,
      min: 0,
      group: 'B — Base',
      helpText: 'Extra effort for cross-team dependencies and known risks.',
    },
    {
      key: 'reviewBufferPercent',
      label: 'Review / testing buffer (%)',
      type: 'multiplier',
      defaultValue: 15,
      min: 0,
      max: 100,
      group: 'B — Base',
      helpText: 'Overhead for code review, tests and rework, as a percentage.',
    },
    {
      key: 'confidencePercent',
      label: 'Confidence (%)',
      type: 'factor',
      defaultValue: 80,
      min: 0,
      max: 100,
      group: 'C — Confidence',
      helpText: 'Estimation confidence 0–100. Lower confidence inflates the days.',
    },
    {
      key: 'calendarWaitingDays',
      label: 'Waiting / calendar (days)',
      type: 'number',
      defaultValue: 3,
      min: 0,
      group: 'W — Waiting',
      helpText: 'Idle calendar time: reviews queued, approvals, blocked waits.',
    },
    {
      key: 'storyPoints',
      label: 'Story points',
      type: 'threshold',
      defaultValue: 5,
      min: 0,
      group: 'W — Waiting',
      helpText: 'Relative size for reference (does not affect the computed days).',
    },
  ],
  outputs: [
    {
      key: 'engineeringEffortDays',
      label: 'Engineering effort (person-days)',
      expression:
        '(baseEngineeringDays + dependencyRiskDays) * (1 + reviewBufferPercent / 100)',
    },
    {
      key: 'confidenceAdjustedDays',
      label: 'Confidence-adjusted (person-days)',
      expression: 'engineeringEffortDays * (100 / confidencePercent)',
    },
    {
      key: 'calendarDays',
      label: 'Calendar days',
      expression: 'confidenceAdjustedDays + calendarWaitingDays',
    },
  ],
};

/**
 * The default TASK template — a per-task work-item schema (title, description,
 * acceptance criteria, story points, priority, status). It is a plain,
 * fully-editable Template document with no computed outputs; users can use it
 * as-is or create their own.
 */
export const DEFAULT_TASK_TEMPLATE: TemplateSeed = {
  name: DEFAULT_TASK_TEMPLATE_NAME,
  description:
    'A standard work-item schema: title, description, acceptance criteria, story points, priority and status. Use as-is or create your own.',
  fields: [
    {
      key: 'title',
      label: 'Title',
      type: 'text',
      defaultValue: '',
      group: 'Details',
      helpText: 'Short, action-oriented summary of the task.',
    },
    {
      key: 'description',
      label: 'Description',
      type: 'text',
      defaultValue: '',
      group: 'Details',
      helpText: 'What needs to be done and why.',
    },
    {
      key: 'acceptanceCriteria',
      label: 'Acceptance criteria',
      type: 'text',
      defaultValue: '',
      group: 'Details',
      helpText: 'Conditions that must be true for the task to be considered done.',
    },
    {
      key: 'storyPoints',
      label: 'Story points',
      type: 'enum',
      defaultValue: '3',
      options: ['1', '2', '3', '5', '8', '13'],
      group: 'Planning',
      helpText: 'Relative size of the task (Fibonacci scale).',
    },
    {
      key: 'priority',
      label: 'Priority',
      type: 'enum',
      defaultValue: 'Medium',
      options: ['Low', 'Medium', 'High', 'Critical'],
      group: 'Planning',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'enum',
      defaultValue: 'Todo',
      options: ['Backlog', 'Todo', 'In Progress', 'In Review', 'Done'],
      group: 'Planning',
    },
  ],
  outputs: [],
};

/** All default template seeds, in seeding order. */
export const DEFAULT_TEMPLATES: TemplateSeed[] = [
  DEFAULT_ESTIMATION_TEMPLATE,
  DEFAULT_TASK_TEMPLATE,
];

/**
 * Seeds the default templates into the store, idempotently: a default is only
 * created when NO version of that template name already exists. Returns the list
 * of names that were newly created. Best-effort per template — a single failure
 * does not abort the rest.
 */
export async function seedDefaultTemplates(
  store: TemplateStore,
): Promise<string[]> {
  const existing = await store.listSummaries();
  const existingNames = new Set(existing.map((s) => s.name));
  const created: string[] = [];
  for (const seed of DEFAULT_TEMPLATES) {
    if (existingNames.has(seed.name)) continue;
    try {
      await store.create(seed);
      created.push(seed.name);
    } catch {
      /* best-effort: skip this default, continue with the rest */
    }
  }
  return created;
}
